// NavigationContext.js

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import {
  clearStoredPostAuthRedirect,
  readStoredPostAuthRedirect,
  writeStoredPostAuthRedirect,
} from 'services/bases/baseInviteRedirectStorage';
import { SectionType } from 'utilities/Enums';
import { NAVIGATION_BREADCRUMB_MAX_STEPS } from 'utilities/Breadcrumbs';

const NavigationContext = createContext(null);

export const NavigationProvider = ({ children }) => {
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [currentNode, setCurrentNode] = useState(null);
  const [backVisible, setBackVisible] = useState(false);
  const [backButtonAction, setBackButtonAction] = useState(null);
  const [currentSection, setCurrentSection] = useState(SectionType.HOME);
  const [stepTitle, setStepTitle] = useState('');
  const [postAuthRedirect, setPostAuthRedirectState] = useState(() => readStoredPostAuthRedirect());

  const setPostAuthRedirect = useCallback((path) => {
    const normalized = path ? String(path).trim() : '';
    if (normalized) {
      writeStoredPostAuthRedirect(normalized);
      setPostAuthRedirectState(normalized);
      return;
    }

    clearStoredPostAuthRedirect();
    setPostAuthRedirectState(null);
  }, []);

  const appendBreadcrumb = useCallback((step) => {
    if (!step || typeof step !== 'object') return;

    const kind = String(step.kind || '').trim();
    const path = String(step.path || '').trim();
    if (!kind || !path) return;

    const key = step.key != null ? String(step.key).trim() : '';
    const label = String(step.label || '').trim() || 'Untitled';
    const typeKey = String(step.typeKey || '').trim();
    const iconKey = String(step.iconKey || '').trim();

    setBreadcrumbs((prev) => {
      const list = Array.isArray(prev) ? prev : [];
      const last = list[list.length - 1];

      const nextStep = {
        kind,
        key,
        path,
        label,
        typeKey,
        iconKey,
        timestamp: Date.now(),
      };

      if (last && last.kind === kind && last.key === key && last.path === path) {
        if (
          last.label === label
          && (last.typeKey || '') === typeKey
          && (last.iconKey || '') === iconKey
        ) return list;
        return [...list.slice(0, -1), { ...last, label, typeKey, iconKey }];
      }

      const appended = [...list, nextStep];
      if (appended.length <= NAVIGATION_BREADCRUMB_MAX_STEPS) return appended;
      return appended.slice(-NAVIGATION_BREADCRUMB_MAX_STEPS);
    });
  }, []);

  const clearBreadcrumbs = useCallback(() => {
    setBreadcrumbs([]);
  }, []);

  const value = useMemo(() => ({
    breadcrumbs,
    appendBreadcrumb,
    clearBreadcrumbs,
    currentNode,
    setCurrentNode,
    backVisible,
    setBackVisible,
    backButtonAction,
    setBackButtonAction,
    currentSection,
    setCurrentSection,
    stepTitle,
    setStepTitle,
    postAuthRedirect,
    setPostAuthRedirect,
  }), [
    breadcrumbs,
    appendBreadcrumb,
    clearBreadcrumbs,
    currentNode,
    backVisible,
    backButtonAction,
    currentSection,
    stepTitle,
    postAuthRedirect,
    setPostAuthRedirect,
  ]);

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

export default NavigationContext;
